import { api } from '../services/api';
import {
  Zone,
  Plot,
  PlantFamily,
  Variety,
  SeedLot,
  CropPlan,
  Crop,
  CropTask,
  Harvest,
  SoilAnalysis,
  InputPurchase,
} from '../types/production.types';

export const productionApi = {
  // ═══════════════════════════════════════════
  // ZONES
  // ═══════════════════════════════════════════

  async listZones(): Promise<Zone[]> {
    return api.get<Zone[]>('/production/zones');
  },

  async getZone(id: string): Promise<Zone> {
    return api.get<Zone>(`/production/zones/${id}`);
  },

  async createZone(data: Partial<Zone>): Promise<Zone> {
    return api.post<Zone>('/production/zones', data);
  },

  async updateZone(id: string, data: Partial<Zone>): Promise<Zone> {
    return api.patch<Zone>(`/production/zones/${id}`, data);
  },

  async deleteZone(id: string): Promise<void> {
    return api.delete(`/production/zones/${id}`);
  },

  // ═══════════════════════════════════════════
  // PLOTS
  // ═══════════════════════════════════════════

  async listPlots(zoneId?: string): Promise<Plot[]> {
    const params = zoneId ? { zone_id: zoneId } : {};
    return api.get<Plot[]>('/production/plots', { params });
  },

  async getPlot(id: string): Promise<Plot> {
    return api.get<Plot>(`/production/plots/${id}`);
  },

  async createPlot(data: Partial<Plot>): Promise<Plot> {
    return api.post<Plot>('/production/plots', data);
  },

  async updatePlot(id: string, data: Partial<Plot>): Promise<Plot> {
    return api.patch<Plot>(`/production/plots/${id}`, data);
  },

  async deletePlot(id: string): Promise<void> {
    return api.delete(`/production/plots/${id}`);
  },

  // ═══════════════════════════════════════════
  // PLANT FAMILIES
  // ═══════════════════════════════════════════

  async listPlantFamilies(): Promise<PlantFamily[]> {
    return api.get<PlantFamily[]>('/production/plant-families');
  },

  async createPlantFamily(data: Partial<PlantFamily>): Promise<PlantFamily> {
    return api.post<PlantFamily>('/production/plant-families', data);
  },

  async updatePlantFamily(id: string, data: Partial<PlantFamily>): Promise<PlantFamily> {
    return api.patch<PlantFamily>(`/production/plant-families/${id}`, data);
  },

  // ═══════════════════════════════════════════
  // VARIETIES
  // ═══════════════════════════════════════════

  async listVarieties(familyId?: string, species?: string): Promise<Variety[]> {
    const params: any = {};
    if (familyId) params.family_id = familyId;
    if (species) params.species = species;
    return api.get<Variety[]>('/production/varieties', { params });
  },

  async getVariety(id: string): Promise<Variety> {
    return api.get<Variety>(`/production/varieties/${id}`);
  },

  async createVariety(data: Partial<Variety>): Promise<Variety> {
    return api.post<Variety>('/production/varieties', data);
  },

  async updateVariety(id: string, data: Partial<Variety>): Promise<Variety> {
    return api.patch<Variety>(`/production/varieties/${id}`, data);
  },

  // ═══════════════════════════════════════════
  // SEED LOTS
  // ═══════════════════════════════════════════

  async listSeedLots(varietyId?: string): Promise<SeedLot[]> {
    const params = varietyId ? { variety_id: varietyId } : {};
    return api.get<SeedLot[]>('/production/seed-lots', { params });
  },

  async getSeedLot(id: string): Promise<SeedLot> {
    return api.get<SeedLot>(`/production/seed-lots/${id}`);
  },

  async createSeedLot(data: Partial<SeedLot>): Promise<SeedLot> {
    return api.post<SeedLot>('/production/seed-lots', data);
  },

  async updateSeedLot(id: string, data: Partial<SeedLot>): Promise<SeedLot> {
    return api.patch<SeedLot>(`/production/seed-lots/${id}`, data);
  },

  async deductSeedLot(id: string, quantity: number): Promise<SeedLot> {
    return api.post<SeedLot>(`/production/seed-lots/${id}/deduct`, { quantity });
  },

  // ═══════════════════════════════════════════
  // CROP PLANS
  // ═══════════════════════════════════════════

  async listCropPlans(): Promise<CropPlan[]> {
    return api.get<CropPlan[]>('/production/crop-plans');
  },

  async getCropPlan(id: string): Promise<CropPlan> {
    return api.get<CropPlan>(`/production/crop-plans/${id}`);
  },

  async createCropPlan(data: { year: number; name?: string; notes?: string }): Promise<CropPlan> {
    return api.post<CropPlan>('/production/crop-plans', data);
  },

  async updateCropPlan(id: string, data: Partial<CropPlan>): Promise<CropPlan> {
    return api.patch<CropPlan>(`/production/crop-plans/${id}`, data);
  },

  async validateCropPlan(id: string, userId?: string): Promise<CropPlan> {
    return api.post<CropPlan>(`/production/crop-plans/${id}/validate`, { user_id: userId });
  },

  // ═══════════════════════════════════════════
  // CROPS
  // ═══════════════════════════════════════════

  async listCrops(filters?: { crop_plan_id?: string; plot_id?: string; status?: string }): Promise<Crop[]> {
    return api.get<Crop[]>('/production/crops', { params: filters });
  },

  async getCrop(id: string): Promise<Crop> {
    return api.get<Crop>(`/production/crops/${id}`);
  },

  async listCropGpsPoints(): Promise<
    { id: string; variety_name: string; variety_species: string; plot_code: string; gps_coordinates: any }[]
  > {
    return api.get('/production/crops/gps-points');
  },

  async createCrop(data: Partial<Crop>): Promise<Crop> {
    return api.post<Crop>('/production/crops', data);
  },

  async updateCrop(id: string, data: Partial<Crop>): Promise<Crop> {
    return api.patch<Crop>(`/production/crops/${id}`, data);
  },

  async updateCropStatus(id: string, status: string): Promise<Crop> {
    return api.patch<Crop>(`/production/crops/${id}/status`, { status });
  },

  // ═══════════════════════════════════════════
  // CROP TASKS
  // ═══════════════════════════════════════════

  async listCropTasks(filters?: { week?: number; completed?: boolean; crop_id?: string }): Promise<CropTask[]> {
    const params: any = {};
    if (filters?.week) params.week = filters.week;
    if (filters?.completed !== undefined) params.completed = String(filters.completed);
    if (filters?.crop_id) params.crop_id = filters.crop_id;
    return api.get<CropTask[]>('/production/crop-tasks', { params });
  },

  async getTasksByWeek(weekNumber: number): Promise<CropTask[]> {
    return api.get<CropTask[]>(`/production/crop-tasks/week/${weekNumber}`);
  },

  async listFieldWorkers(): Promise<{ id: string; name: string; role: string }[]> {
    return api.get('/production/field-workers');
  },

  async updateCropTask(id: string, data: Partial<CropTask>): Promise<CropTask> {
    return api.patch<CropTask>(`/production/crop-tasks/${id}`, data);
  },

  async completeCropTask(id: string, completedBy?: string): Promise<CropTask> {
    return api.post<CropTask>(`/production/crop-tasks/${id}/complete`, { completed_by: completedBy });
  },

  // ═══════════════════════════════════════════
  // HARVESTS
  // ═══════════════════════════════════════════

  async listHarvests(cropId?: string): Promise<Harvest[]> {
    const params = cropId ? { crop_id: cropId } : {};
    return api.get<Harvest[]>('/production/harvests', { params });
  },

  async getHarvest(id: string): Promise<Harvest> {
    return api.get<Harvest>(`/production/harvests/${id}`);
  },

  async createHarvest(data: {
    crop_id: string;
    harvest_date: string;
    quantity_kg: number;
    quality_grade?: string;
    notes?: string;
  }): Promise<Harvest> {
    return api.post<Harvest>('/production/harvests', data);
  },

  // ═══════════════════════════════════════════
  // SOIL ANALYSES
  // ═══════════════════════════════════════════

  async listSoilAnalyses(plotId?: string): Promise<SoilAnalysis[]> {
    const params = plotId ? { plot_id: plotId } : {};
    return api.get<SoilAnalysis[]>('/production/soil-analyses', { params });
  },

  async createSoilAnalysis(data: Partial<SoilAnalysis>): Promise<SoilAnalysis> {
    return api.post<SoilAnalysis>('/production/soil-analyses', data);
  },

  // ═══════════════════════════════════════════
  // INPUT PURCHASES (achats d'intrants)
  // ═══════════════════════════════════════════

  async listInputPurchases(filters?: { category?: string; year?: number }): Promise<InputPurchase[]> {
    const params: any = {};
    if (filters?.category) params.category = filters.category;
    if (filters?.year) params.year = filters.year;
    return api.get<InputPurchase[]>('/production/input-purchases', { params });
  },

  async createInputPurchase(data: Partial<InputPurchase>): Promise<InputPurchase> {
    return api.post<InputPurchase>('/production/input-purchases', data);
  },

  async updateInputPurchase(id: string, data: Partial<InputPurchase>): Promise<InputPurchase> {
    return api.patch<InputPurchase>(`/production/input-purchases/${id}`, data);
  },

  async deleteInputPurchase(id: string): Promise<void> {
    return api.delete(`/production/input-purchases/${id}`);
  },

  async attachInputPurchaseDocument(id: string, file: File): Promise<InputPurchase> {
    const formData = new FormData();
    formData.append('file', file);
    return api.post<InputPurchase>(`/production/input-purchases/${id}/attach-document`, formData);
  },
};
